import { useQuery } from "@tanstack/react-query";
import type { DashboardMode } from "@nexus/contracts";
import { useIamPermission, usePermissions } from "@/features/auth/hooks/usePermissions";
import { writeActivePresetId } from "@/features/dashboard/lib/active-preset-storage";
import { buildCurrentPresetPayload } from "@/features/overview/lib/filter-preset-utils";
import {
  overviewFilterPresetsQueryOptions,
  useCreateOverviewFilterPreset,
} from "@/lib/queries/filter-presets.queries";
import { SaveFilterPresetControl } from "./SaveFilterPresetControl";

interface OverviewSaveFilterPresetProps {
  mode: DashboardMode;
  canSavePreset?: boolean;
  onPresetCreated?: (presetId: string) => void;
}

export function OverviewSaveFilterPreset({
  mode,
  canSavePreset = true,
  onPresetCreated,
}: OverviewSaveFilterPresetProps) {
  const canListPresets = useIamPermission(usePermissions().OVERVIEW_VIEW_PAGE);
  const createMutation = useCreateOverviewFilterPreset(mode);
  const { data: presets = [] } = useQuery({
    ...overviewFilterPresetsQueryOptions(mode),
    enabled: canListPresets,
  });

  if (!canListPresets) return null;

  return (
    <SaveFilterPresetControl
      disabled={!canSavePreset}
      presets={presets}
      isPending={createMutation.isPending}
      i18nPrefix="overview.filterPresets"
      onSave={async (name) => {
        const payload = buildCurrentPresetPayload(mode);
        const created = await createMutation.mutateAsync({
          name,
          ...payload,
        });
        writeActivePresetId("overview", mode, created.id);
        onPresetCreated?.(created.id);
      }}
    />
  );
}
